export type BattleUnitTypeConfig = {
    id: string;
    labelKey: string;
    power: number;
    foodPerHex: number;
    weaponCost: number;
};

export type BattleEnemyTypeConfig = BattleUnitTypeConfig & {
    weight: number;
    countRange: [number, number];
};

export type BuildingStorageConfig = {
    // max defines max number of this resource this building can store
    max: number;
    // cap defines the default production cap for this resource (<= max)
    cap?: number;
    // batch defines what single batch can produce, defined where
    batch?: number;
};

export type BuildingConfig = {
    id: string;
    cost?: Record<string, number>;
    productionMode?: 'independent' | 'cooperative';
    costScaling?: {
        a: number;
        b: number;
    };
    input?: Record<string, BuildingStorageConfig>;
    output?: Record<string, BuildingStorageConfig>;
};

export type GameConfig = {
    ui: {
        language: 'en' | 'pl';
        storageStatsMinutes: number;
    };
    audio: {
        background: { enabled: boolean; volume: number };
        bell: { enabled: boolean; volume: number };
        hit: { enabled: boolean; volume: number };
    };
    time: {
        tickSeconds: number;
        ticksPerMinute: number;
        ticksPerHour: number;
    };
    map: {
        size: 'small' | 'medium' | 'huge';
        seed?: number;
    };
    buildings: {
        lumberjackCamp: BuildingConfig;
        sawmill: BuildingConfig;
        woodHouse: BuildingConfig;
        stoneHouse: BuildingConfig;
        farm: BuildingConfig;
        huntingHut: BuildingConfig;
        fishingHut: BuildingConfig;
        smokehouse: BuildingConfig;
        weaver: BuildingConfig;
        blacksmith: BuildingConfig;
        goldMine: BuildingConfig;
        mint: BuildingConfig;
        caravanPost: BuildingConfig;
        stable: BuildingConfig;
        quarry: BuildingConfig;
        masonry: BuildingConfig;
        charcoalPit: BuildingConfig;
        ironMine: BuildingConfig;
        smelter: BuildingConfig;
        barracks: BuildingConfig;
    };
    construction: {
        refund: {
            percent: number;
            allowInProgress: boolean;
        };
    };
    production: {
        lumberWorkPerWood: number;
        sawmillWorkPerBatch: number;
        builderWorkPerTick: number;
        forester: {
            ticksPerTree: number;
        };
        populationGrowthPerTick: number;
        farm: {
            ticksPerFarmer: number;
            toolUsage: number;
        };
        hunter: {
            ticksPerHuntRatio: { ticks: number; wildLife: number };
            minHuntTicks: number;
            foodPerWildLife: number;
            leatherPerWildLife: number;
            toolUsage: number;
        };
        fishing: {
            ticksPerCatch: number;
            fishPerCatch: number;
        };
        smokehouse: {
            ticksPerBatch: number;
            fishPerBatch: number;
            foodPerBatch: number;
        };
        blacksmith: {
            toolTicks: number;
            toolIngots: number;
            toolWood: number;
            toolsPerBatch: number;
            weaponTicks: number;
            weaponIngots: number;
            weaponWood: number;
            weaponLeather: number;
            weaponsPerBatch: number;
        };
        goldMine: {
            ticksPerOre: number;
        };
        mint: {
            ticksPerBatch: number;
        };
        weaver: {
            ticksPerBatch: number;
        };
        quarry: {
            ticksPerStone: number;
        };
        masonry: {
            ticksPerBrick: number;
        };
        charcoal: {
            ticksPerBatch: number;
        };
        ironMine: {
            ticksPerOre: number;
        };
        smelter: {
            ticksPerBatch: number;
            orePerBatch: number;
            charcoalPerBatch: number;
            ingotsPerBatch: number;
        };
    };
    tree: {
        ageInterval: number;
        maxAge: number;
        peakAge: number;
        cutWorkBase: number;
        cutWorkPerAge: number;
        yieldBase: number;
        yieldAgeFactor: number;
        yieldMinMultiplier: number;
        yieldTailMultiplier: number;
        reseedInterval: number;
        reseedThreshold: number;
        reseedRate: number;
    };
    wildLife: {
        matingInterval: number;
        ageTicks: number;
        migrationInterval: number;
        migrationRadius: number;
        migrationRate: number;
        treesPerAnimal: number;
        biomeCapWeight: number;
        targetFill: number;
        matingAgeStart: number;
        fecundityPerAdult: number;
        reproductionDensityPower: number;
        newbornAge: number;
        baseMortalityPerAgeTick: number;
        juvenileAgeEnd: number;
        juvenileExtraMortality: number;
        senescentAgeStart: number;
        senescentMortalitySlope: number;
        maxAge: number;
        overCapacityCullRate: number;
        colonizeEmptyChance: number;
        colonizeEmptyMin: number;
        colonizeEmptyMax: number;
        huntableAgeMin: number;
    };
    fish: {
        regenRate: number;
    };
    tool: {
        initialDurability: number;
    };
    expedition: {
        baseTicks: number;
        ticksPerHex: number;
        foodPerPopPerHex: number;
        toolsRequired: number;
        encounters: {
            banditLossRange: [number, number];
            fatalFactions: string[];
            stealAll: boolean;
        };
    };
    scouting: {
        baseTicks: number;
        ticksPerHex: number;
        foodMultiplier: number;
        foodStep: number;
    };
    battle: {
        roundTicks: number;
        unitsPerRound: number;
        minRounds: number;
        maxRounds: number;
        rollMin: number;
        rollMax: number;
        encounterChance: number;
        encounterScaling: {
            maxDistance: number;
            minChance: number;
            maxChance: number;
            minCountMultiplier: number;
            maxCountMultiplier: number;
            countJitter: number;
        };
        units: Record<string, BattleUnitTypeConfig>;
        enemies: Record<string, BattleEnemyTypeConfig>;
    };
    alerts: {
        warningCooldownTicks: number;
        starvationHours: number;
        coldDebtRatio: number;
    };
    transport: {
        baseTicks: number;
        ticksPerHex: number;
        departureInterval: number;

        /**
         * Assistants are local haulers that move goods between buildings/storage/houses.
         * We intentionally keep the model coarse (distance classes) to avoid per-hex pathing.
         */
        assistants: {
            /** one-way travel time (empty) in ticks */
            distanceTicks: {
                near: number;
                city: number;
                remote: number;
            };
            /** extra travel time penalty (e.g. 0.33 means +33%) when carrying payload */
            payloadPenaltyPercent: number;
            /** optional default distance class per buildingId (SettlementBuildingKey) */
            distanceByBuilding: Record<string, 'near' | 'city' | 'remote'>;
        };
    };
    defaults: {
        populationPerHouse: number;
        houseFoodTargetHours: number;
        houseHeatTargetHours: number;
        blacksmithToolTarget: number;
        blacksmithWeaponTarget: number;
    };
    population: {
        hunger: {
            // hungerInterval defines how often one gets hungry
            hungerInterval: number;
            // hungerStep defines by how much one gets hungry
            hungerStep: number;
            // eatThreshold defines how hungry must one be to eat
            eatThreshold: number;
            // foodStep defines by how much one heals hunger with one food
            foodStep: number;
            // foodQty defines how much one eats per meal
            foodQty: number;
            // maxHunger defines at what hunger man die
            maxHunger: number;
        };

        warmth: {
            // maxWarmth defines what is initial warmth for man
            maxWarmth: number;
            // coldInterval defines interval in which one gets cold
            coldInterval: number;
            // coldStep is a step by which one gets gold
            coldStep: number;
            // warmthStep defines at what step one restores warmth (if coldDebtThreshold is below)
            warmthStep: number;

            // coldDebtThreshold defines at what warmth level people get cold so eith that start to use coldInterval + coldStep
            coldDebtThreshold: number;
        };
    };
    heat: {
        // coldInterval defines how often house loses warmth
        coldInterval: number;

        // coldStep defines how much warmth house loses
        coldStep: {
            // wooden defines step for wooden house
            wooden: number;
            // stone defines step for stone house
            stone: number;
        };

        // warmInterval defines how often house gets warmed
        warmInterval: number;

        // maxWarmth defines what is max warmth capacity for a house
        maxWarmth: {
            wooden: number;
            stone: number;
        };

        // warmth defines how much warmth house gets with one unit or resource
        warmth: {
            wood: number;
            charcoal: number;
        };
    };
    initial: {
        settlement: {
            population: number;
            tier: 'outpost' | 'town' | 'city';
            buildings: {
                houses: number;
                stoneHouse: number;
                storage: boolean;
                lumberjack: number;
                sawmill: number;
                farm: number;
                huntingHut: number;
                fishingHut: number;
                smokehouse: number;
                weaver: number;
                blacksmith: number;
                goldMine: number;
                mint: number;
                caravanPost: number;
                stable: number;
                quarry: number;
                masonry: number;
                charcoalPit: number;
                ironMine: number;
                smelter: number;
                barracks: number;
            };
        };
        inventory: {
            food: number;
            fish: number;
            tools: number;
            planks: number;
            wood: number;
            stone: number;
            bricks: number;
            charcoal: number;
            ironOre: number;
            ironIngots: number;
            fiber: number;
            clothRope: number;
            leather: number;
            weapons: number;
            goldOre: number;
            coins: number;
        };
    };
};

export type ScoutingConfig = GameConfig['scouting'];

const STORAGE_KEY = 'fantasyKingdomConfig';
const STORAGE_HASH_KEY = 'fantasyKingdomConfigHash';

export type ConfigError = { key: string; args?: Array<string | number> };

export const DEFAULT_CONFIG: GameConfig = {
    ui: {
        language: 'en',
        storageStatsMinutes: 10,
    },
    audio: {
        background: { enabled: true, volume: 0.4 },
        bell: { enabled: true, volume: 0.8 },
        hit: { enabled: true, volume: 0.6 },
    },
    time: {
        tickSeconds: 0.1,
        ticksPerMinute: 600,
        ticksPerHour: 36000,
    },
    map: {
        size: 'small',
    },
    buildings: {
        lumberjackCamp: {
            id: 'lumberjackCamp',
            cost: { planks: 10 },
            productionMode: 'cooperative',
            output: {
                wood: { max: 120 },
            },
        },
        sawmill: {
            id: 'sawmill',
            cost: { wood: 50 },
            productionMode: 'cooperative',
            input: {
                wood: { max: 100, batch: 1 },
            },
            output: {
                planks: { max: 200, cap: 100, batch: 2 },
            },
        },
        woodHouse: {
            id: 'woodHouse',
            costScaling: { a: 0.06, b: 0.002 },
            cost: { planks: 12 },
        },
        stoneHouse: {
            id: 'stoneHouse',
            costScaling: { a: 0.05, b: 0.0015 },
            cost: { bricks: 12, stone: 8 },
        },
        farm: {
            id: 'farm',
            cost: { wood: 10 },
            productionMode: 'independent',

            output: {
                food: { max: 500, batch: 10 },
            },
        },
        huntingHut: {
            id: 'huntingHut',
            cost: { wood: 10 },
            productionMode: 'independent',

            // output is based on hunt result
            output: {
                food: { max: 50 },
                leather: { max: 50 },
            },
        },
        fishingHut: {
            id: 'fishingHut',
            cost: { planks: 20, wood: 10 },
            productionMode: 'independent',
            // input is based on region resources

            output: {
                fish: {
                    max: 50,
                },
            },
        },
        smokehouse: {
            id: 'smokehouse',
            cost: { planks: 15, stone: 10 },
            input: {
                fish: { max: 100, batch: 2 },
            },
            output: {
                food: { max: 100, batch: 2 },
            },
        },
        weaver: {
            id: 'weaver',
            cost: { planks: 30, clothRope: 10 },

            input: {
                fiber: { batch: 3, max: 100 },
            },

            output: {
                clothRope: { batch: 1, max: 100 },
            },
        },
        blacksmith: {
            id: 'blacksmith',
            cost: { planks: 50, bricks: 20, ironIngots: 10 },
            input: {
                wood: { max: 10 },
                ironIngots: { max: 10 },
                leather: { max: 10 },
            },
            output: {
                tools: { max: 6 },
                weapons: { max: 6 },
            },
        },
        goldMine: {
            id: 'goldMine',
            cost: { planks: 60, bricks: 40, tools: 10 },
            // input is based on what region resources

            output: {
                // batch not defined on purpose, it is defined in gold mine script
                goldOre: { max: 200 },
            },
        },
        mint: {
            id: 'mint',
            cost: { planks: 80, bricks: 60 },
            input: {
                goldOre: { batch: 1, max: 20 },
            },
            output: {
                coins: { batch: 5, max: 200 },
            },
        },
        caravanPost: {
            id: 'caravanPost',
            cost: { planks: 30, clothRope: 10 },
        },
        stable: {
            id: 'stable',
            cost: { planks: 40, bricks: 10 },
        },
        quarry: {
            id: 'quarry',
            cost: { planks: 20, wood: 10 },
            // input is based on what region resources

            output: {
                stone: { max: 250 },
            },
        },
        masonry: {
            id: 'masonry',
            cost: { planks: 30, stone: 30 },
            input: {
                stone: { max: 50, batch: 3 },
            },
            output: {
                bricks: { max: 150, batch: 1 },
            },
        },
        charcoalPit: {
            id: 'charcoalPit',
            cost: { planks: 20 },
            input: {
                wood: { max: 20, batch: 6 },
            },
            output: {
                charcoal: { max: 20, batch: 2 },
            },
        },
        ironMine: {
            id: 'ironMine',
            cost: { planks: 30, stone: 20 },
            output: {
                ironOre: { max: 200 },
            },
        },
        smelter: {
            id: 'smelter',
            cost: { planks: 30, stone: 30, charcoal: 10 },
            input: {
                ironOre: { max: 60, batch: 3 },
                charcoal: { max: 40, batch: 1 },
            },
            output: {
                ironIngots: { max: 50, batch: 1 },
            },
        },
        barracks: {
            id: 'barracks',
            cost: { planks: 40, bricks: 10 },
            // There is no output
        },
    },
    construction: {
        refund: {
            percent: 100,
            allowInProgress: true,
        },
    },
    production: {
        lumberWorkPerWood: 2,
        sawmillWorkPerBatch: 28,
        builderWorkPerTick: 1,
        forester: {
            ticksPerTree: 120,
        },
        populationGrowthPerTick: 0.002,
        farm: {
            ticksPerFarmer: 25,
            toolUsage: 0.25,
        },
        hunter: {
            ticksPerHuntRatio: { ticks: 5, wildLife: 25 },
            minHuntTicks: 2,
            foodPerWildLife: 1,
            leatherPerWildLife: 0.5,
            toolUsage: 0.25,
        },
        fishing: {
            ticksPerCatch: 180,
            fishPerCatch: 1,
        },
        smokehouse: {
            ticksPerBatch: 60,
            fishPerBatch: 2,
            foodPerBatch: 2,
        },
        blacksmith: {
            toolTicks: 30,
            toolIngots: 1,
            toolWood: 1,
            toolsPerBatch: 1,
            weaponTicks: 30,
            weaponIngots: 2,
            weaponWood: 1,
            weaponLeather: 1,
            weaponsPerBatch: 1,
        },
        goldMine: {
            ticksPerOre: 300,
        },
        mint: {
            ticksPerBatch: 180,
        },
        weaver: {
            ticksPerBatch: 180,
        },
        quarry: {
            ticksPerStone: 90,
        },
        masonry: {
            ticksPerBrick: 120,
        },
        charcoal: {
            ticksPerBatch: 4,
        },
        ironMine: {
            ticksPerOre: 120,
        },
        smelter: {
            ticksPerBatch: 180,
            orePerBatch: 3,
            charcoalPerBatch: 1,
            ingotsPerBatch: 2,
        },
    },
    tree: {
        ageInterval: 240,
        maxAge: 24,
        peakAge: 12,
        cutWorkBase: 80,
        cutWorkPerAge: 6,
        yieldBase: 2,
        yieldAgeFactor: 0.2,
        yieldMinMultiplier: 0.4,
        yieldTailMultiplier: 0.6,
        reseedInterval: 720,
        reseedThreshold: 0.7,
        reseedRate: 0.01,
    },
    wildLife: {
        matingInterval: 120,
        ageTicks: 30,
        migrationInterval: 60,
        migrationRadius: 1,
        migrationRate: 0.08,
        treesPerAnimal: 12,
        biomeCapWeight: 0.8,
        targetFill: 0.95,
        matingAgeStart: 6,
        fecundityPerAdult: 0.9,
        reproductionDensityPower: 2.4,
        newbornAge: 0,
        baseMortalityPerAgeTick: 0.004,
        juvenileAgeEnd: 6,
        juvenileExtraMortality: 0.004,
        senescentAgeStart: 16,
        senescentMortalitySlope: 0.04,
        maxAge: 24,
        overCapacityCullRate: 0.7,
        colonizeEmptyChance: 0.25,
        colonizeEmptyMin: 2,
        colonizeEmptyMax: 6,
        huntableAgeMin: 3,
    },
    fish: {
        regenRate: 0.05,
    },
    tool: {
        initialDurability: 100,
    },
    expedition: {
        baseTicks: 200,
        ticksPerHex: 30,
        foodPerPopPerHex: 1,
        toolsRequired: 0,
        encounters: {
            banditLossRange: [1, 3],
            fatalFactions: ['goblins', 'orcs'],
            stealAll: true,
        },
    },
    scouting: {
        baseTicks: 30,
        ticksPerHex: 10,
        foodMultiplier: 1,
        foodStep: 10,
    },
    battle: {
        roundTicks: 20,
        unitsPerRound: 6,
        minRounds: 2,
        maxRounds: 6,
        rollMin: 0.2,
        rollMax: 0.6,
        encounterChance: 0.08,
        encounterScaling: {
            maxDistance: 12,
            minChance: 0.08,
            maxChance: 0.24,
            minCountMultiplier: 1,
            maxCountMultiplier: 2.4,
            countJitter: 0.25,
        },
        units: {
            militia: {
                id: 'militia',
                labelKey: 'unit.militia',
                power: 1,
                foodPerHex: 1,
                weaponCost: 1,
            },
        },
        enemies: {
            bandits: {
                id: 'bandits',
                labelKey: 'enemy.bandits',
                power: 0.8,
                foodPerHex: 0,
                weaponCost: 0,
                weight: 0.5,
                countRange: [3, 6],
            },
            goblins: {
                id: 'goblins',
                labelKey: 'enemy.goblins',
                power: 0.7,
                foodPerHex: 0,
                weaponCost: 0,
                weight: 0.3,
                countRange: [4, 8],
            },
            orcs: {
                id: 'orcs',
                labelKey: 'enemy.orcs',
                power: 1.2,
                foodPerHex: 0,
                weaponCost: 0,
                weight: 0.2,
                countRange: [2, 5],
            },
        },
    },
    alerts: {
        warningCooldownTicks: 120,
        starvationHours: 2,
        coldDebtRatio: 0.6,
    },
    transport: {
        baseTicks: 40,
        ticksPerHex: 20,
        departureInterval: 60,
        assistants: {
            distanceTicks: {
                near: 3,
                city: 6,
                remote: 12,
            },
            payloadPenaltyPercent: 0.33,
            // These are conservative defaults; override per-map/biome/placement if you model distances elsewhere.
            // Keys are SettlementBuildingKey (string ids used in settlement.buildings / getBuildingStorage).
            distanceByBuilding: {
                // core / near
                lumberjack: 'near',
                sawmill: 'near',
                charcoalPit: 'near',
                blacksmith: 'near',
                smokehouse: 'near',
                weaver: 'near',
                farm: 'near',
                houses: 'near',
                inventory: 'near',

                // typically outside core
                huntingHut: 'city',
                fishingHut: 'city',
                quarry: 'city',
                masonry: 'city',
                mint: 'city',

                // remote / heavy industry
                ironMine: 'remote',
                smelter: 'remote',
                goldMine: 'remote',
                barracks: 'remote',
            },
        },
    },
    defaults: {
        populationPerHouse: 2,
        houseFoodTargetHours: 2,
        houseHeatTargetHours: 1,
        blacksmithToolTarget: 0,
        blacksmithWeaponTarget: 0,
    },
    population: {
        hunger: {
            // hungerInterval defines how often one gets hungry
            hungerInterval: 15,
            // hungerStep defines by how much one gets hungry
            hungerStep: 0.25,
            // maxHunger defines at what hunger man die
            maxHunger: 12,
            // eatThreshold defines how hungry must one be to eat
            eatThreshold: 2,
            // foodStep defines by how much one heals hunger with one food
            foodStep: 2.25,
            // foodQty defines how much one eats per meal
            foodQty: 1.2,
        },
        warmth: {
            // maxWarmth defines what is initial warmth for man
            maxWarmth: 10,
            // coldInterval defines interval in which one gets cold
            coldInterval: 50,
            // coldStep is a step by which one gets cold
            coldStep: 1,
            // warmthStep defines at what step one restores warmth (if coldDebtThreshold is below)
            warmthStep: 15,
            // coldDebtThreshold defines at what warmth level people get cold
            coldDebtThreshold: 8,
        },
    },
    heat: {
        coldInterval: 25,
        coldStep: {
            wooden: 0.18,
            stone: 0.12,
        },
        warmInterval: 12,
        maxWarmth: {
            wooden: 20,
            stone: 30,
        },
        warmth: {
            wood: 2,
            charcoal: 4,
        },
    },
    initial: {
        settlement: {
            population: 10,
            tier: 'town',
            buildings: {
                houses: 5,
                stoneHouse: 0,
                storage: true,
                lumberjack: 1,
                sawmill: 1,
                farm: 2,
                huntingHut: 0,
                fishingHut: 0,
                smokehouse: 0,
                weaver: 0,
                blacksmith: 0,
                goldMine: 0,
                mint: 0,
                caravanPost: 0,
                stable: 0,
                quarry: 0,
                masonry: 0,
                charcoalPit: 0,
                ironMine: 0,
                smelter: 0,
                barracks: 0,
            },
        },
        inventory: {
            food: 120,
            fish: 0,
            tools: 60,
            planks: 80,
            wood: 30,
            stone: 0,
            bricks: 0,
            charcoal: 120,
            ironOre: 0,
            ironIngots: 0,
            fiber: 20,
            clothRope: 10,
            leather: 0,
            weapons: 0,
            goldOre: 0,
            coins: 25,
        },
    },
};

let cachedConfig: GameConfig | null = null;

function hashDefaultConfig(): string {
    const text = JSON.stringify(DEFAULT_CONFIG);
    let hash = 2166136261;
    for (let i = 0; i < text.length; i += 1) {
        hash ^= text.charCodeAt(i);
        hash = Math.imul(hash, 16777619);
    }
    return hash.toString(16);
}

function mergeStorageConfig(
    base?: Record<string, BuildingStorageConfig>,
    next?: Record<string, BuildingStorageConfig>,
): Record<string, BuildingStorageConfig> | undefined {
    if (!base && !next) return undefined;
    const merged: Record<string, BuildingStorageConfig> = { ...(base || {}) };
    if (next) {
        Object.entries(next).forEach(([key, value]) => {
            merged[key] = { ...(base?.[key] || { max: 0 }), ...(value || {}) };
        });
    }
    return merged;
}

function mergeBuildingConfig(base: BuildingConfig, next?: BuildingConfig): BuildingConfig {
    if (!next) return base;
    return {
        ...base,
        ...next,
        cost: { ...(base.cost || {}), ...(next.cost || {}) },
        costScaling: base.costScaling
            ? { ...base.costScaling, ...(next.costScaling || {}) }
            : next.costScaling,
        input: mergeStorageConfig(base.input, next.input),
        output: mergeStorageConfig(base.output, next.output),
    };
}

export function mergeGameConfig(base: GameConfig, next: Partial<GameConfig>): GameConfig {
    return {
        ui: { ...base.ui, ...(next.ui || {}) },
        audio: {
            ...base.audio,
            ...(next.audio || {}),
            background: { ...base.audio.background, ...(next.audio?.background || {}) },
            bell: { ...base.audio.bell, ...(next.audio?.bell || {}) },
            hit: { ...base.audio.hit, ...(next.audio?.hit || {}) },
        },
        time: { ...base.time, ...(next.time || {}) },
        map: { ...base.map, ...(next.map || {}) },
        buildings: {
            ...base.buildings,
            ...(next.buildings || {}),
            lumberjackCamp: mergeBuildingConfig(
                base.buildings.lumberjackCamp,
                next.buildings?.lumberjackCamp,
            ),
            sawmill: mergeBuildingConfig(base.buildings.sawmill, next.buildings?.sawmill),
            woodHouse: mergeBuildingConfig(base.buildings.woodHouse, next.buildings?.woodHouse),
            stoneHouse: mergeBuildingConfig(base.buildings.stoneHouse, next.buildings?.stoneHouse),
            farm: mergeBuildingConfig(base.buildings.farm, next.buildings?.farm),
            huntingHut: mergeBuildingConfig(base.buildings.huntingHut, next.buildings?.huntingHut),
            fishingHut: mergeBuildingConfig(base.buildings.fishingHut, next.buildings?.fishingHut),
            smokehouse: mergeBuildingConfig(base.buildings.smokehouse, next.buildings?.smokehouse),
            weaver: mergeBuildingConfig(base.buildings.weaver, next.buildings?.weaver),
            blacksmith: mergeBuildingConfig(base.buildings.blacksmith, next.buildings?.blacksmith),
            goldMine: mergeBuildingConfig(base.buildings.goldMine, next.buildings?.goldMine),
            mint: mergeBuildingConfig(base.buildings.mint, next.buildings?.mint),
            caravanPost: mergeBuildingConfig(
                base.buildings.caravanPost,
                next.buildings?.caravanPost,
            ),
            stable: mergeBuildingConfig(base.buildings.stable, next.buildings?.stable),
            quarry: mergeBuildingConfig(base.buildings.quarry, next.buildings?.quarry),
            masonry: mergeBuildingConfig(base.buildings.masonry, next.buildings?.masonry),
            charcoalPit: mergeBuildingConfig(
                base.buildings.charcoalPit,
                next.buildings?.charcoalPit,
            ),
            ironMine: mergeBuildingConfig(base.buildings.ironMine, next.buildings?.ironMine),
            smelter: mergeBuildingConfig(base.buildings.smelter, next.buildings?.smelter),
            barracks: mergeBuildingConfig(base.buildings.barracks, next.buildings?.barracks),
        },
        construction: {
            ...base.construction,
            ...(next.construction || {}),
            refund: { ...base.construction.refund, ...(next.construction?.refund || {}) },
        },
        production: {
            ...base.production,
            ...(next.production || {}),
            farm: { ...base.production.farm, ...(next.production?.farm || {}) },
            hunter: { ...base.production.hunter, ...(next.production?.hunter || {}) },
            fishing: { ...base.production.fishing, ...(next.production?.fishing || {}) },
            smokehouse: { ...base.production.smokehouse, ...(next.production?.smokehouse || {}) },
            blacksmith: { ...base.production.blacksmith, ...(next.production?.blacksmith || {}) },
            goldMine: { ...base.production.goldMine, ...(next.production?.goldMine || {}) },
            mint: { ...base.production.mint, ...(next.production?.mint || {}) },
            weaver: { ...base.production.weaver, ...(next.production?.weaver || {}) },
            forester: { ...base.production.forester, ...(next.production?.forester || {}) },
            quarry: { ...base.production.quarry, ...(next.production?.quarry || {}) },
            masonry: { ...base.production.masonry, ...(next.production?.masonry || {}) },
            charcoal: { ...base.production.charcoal, ...(next.production?.charcoal || {}) },
            ironMine: { ...base.production.ironMine, ...(next.production?.ironMine || {}) },
            smelter: { ...base.production.smelter, ...(next.production?.smelter || {}) },
        },
        defaults: { ...base.defaults, ...(next.defaults || {}) },
        population: {
            ...base.population,
            ...(next.population || {}),
            hunger: { ...base.population.hunger, ...(next.population?.hunger || {}) },
            warmth: { ...base.population.warmth, ...(next.population?.warmth || {}) },
        },
        heat: {
            ...base.heat,
            ...(next.heat || {}),
            coldStep: { ...base.heat.coldStep, ...(next.heat?.coldStep || {}) },
            maxWarmth: { ...base.heat.maxWarmth, ...(next.heat?.maxWarmth || {}) },
            warmth: { ...base.heat.warmth, ...(next.heat?.warmth || {}) },
        },
        initial: {
            ...base.initial,
            ...(next.initial || {}),
            settlement: {
                ...base.initial.settlement,
                ...(next.initial?.settlement || {}),
                buildings: {
                    ...base.initial.settlement.buildings,
                    ...(next.initial?.settlement?.buildings || {}),
                },
            },
            inventory: { ...base.initial.inventory, ...(next.initial?.inventory || {}) },
        },
        tree: { ...base.tree, ...(next.tree || {}) },
        wildLife: { ...base.wildLife, ...(next.wildLife || {}) },
        fish: { ...base.fish, ...(next.fish || {}) },
        tool: { ...base.tool, ...(next.tool || {}) },
        expedition: {
            ...base.expedition,
            ...(next.expedition || {}),
            encounters: { ...base.expedition.encounters, ...(next.expedition?.encounters || {}) },
        },
        scouting: { ...base.scouting, ...(next.scouting || {}) },
        battle: {
            ...base.battle,
            ...(next.battle || {}),
            encounterScaling: {
                ...base.battle.encounterScaling,
                ...(next.battle?.encounterScaling || {}),
            },
            units: { ...base.battle.units, ...(next.battle?.units || {}) },
            enemies: { ...base.battle.enemies, ...(next.battle?.enemies || {}) },
        },
        alerts: { ...base.alerts, ...(next.alerts || {}) },
        transport: {
            ...base.transport,
            ...(next.transport || {}),
            assistants: {
                ...base.transport.assistants,
                ...(next.transport?.assistants || {}),
                distanceTicks: {
                    ...base.transport.assistants.distanceTicks,
                    ...(next.transport?.assistants?.distanceTicks || {}),
                },
                distanceByBuilding: {
                    ...base.transport.assistants.distanceByBuilding,
                    ...(next.transport?.assistants?.distanceByBuilding || {}),
                },
            },
        },
    };
}

export function getGameConfig(): GameConfig {
    if (!cachedConfig) cachedConfig = DEFAULT_CONFIG;
    return cachedConfig;
}

export function setGameConfig(next: GameConfig): void {
    cachedConfig = next;
}

export function loadGameConfig(): GameConfig {
    if (typeof window === 'undefined') {
        cachedConfig = DEFAULT_CONFIG;
        return cachedConfig;
    }
    const defaultHash = hashDefaultConfig();
    try {
        const storedHash = window.localStorage.getItem(STORAGE_HASH_KEY);
        if (storedHash && storedHash !== defaultHash) {
            window.localStorage.removeItem(STORAGE_KEY);
            window.localStorage.setItem(STORAGE_KEY, JSON.stringify(DEFAULT_CONFIG));
            window.localStorage.setItem(STORAGE_HASH_KEY, defaultHash);
            cachedConfig = DEFAULT_CONFIG;
            return cachedConfig;
        }
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) {
            window.localStorage.setItem(STORAGE_HASH_KEY, defaultHash);
            cachedConfig = DEFAULT_CONFIG;
            return cachedConfig;
        }
        const parsed = JSON.parse(raw) as Partial<GameConfig>;
        cachedConfig = mergeGameConfig(DEFAULT_CONFIG, parsed || {});
        window.localStorage.setItem(STORAGE_HASH_KEY, defaultHash);
        return cachedConfig;
    } catch {
        window.localStorage.removeItem(STORAGE_KEY);
        window.localStorage.setItem(STORAGE_HASH_KEY, defaultHash);
        cachedConfig = DEFAULT_CONFIG;
        return cachedConfig;
    }
}

export function saveGameConfig(config: GameConfig): void {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
    window.localStorage.setItem(STORAGE_HASH_KEY, hashDefaultConfig());
}

export function resetGameConfig(): GameConfig {
    cachedConfig = DEFAULT_CONFIG;
    if (typeof window !== 'undefined') {
        window.localStorage.removeItem(STORAGE_KEY);
        window.localStorage.removeItem(STORAGE_HASH_KEY);
    }
    return cachedConfig;
}

export function validateGameConfig(config: GameConfig): ConfigError | null {
    const checkCost = (
        cost: Record<string, number> | undefined,
        label: string,
    ): ConfigError | null => {
        if (!cost || typeof cost !== 'object') {
            return { key: 'config.error.buildings.cost.object', args: [label] };
        }
        for (const [key, value] of Object.entries(cost)) {
            if (!Number.isFinite(value) || value < 0) {
                return { key: 'config.error.buildings.cost.value', args: [label, key] };
            }
        }
        return null;
    };

    const checkStorage = (
        storage: Record<string, BuildingStorageConfig> | undefined,
        building: string,
        kind: 'input' | 'output',
    ): ConfigError | null => {
        if (!storage) return null;
        for (const [resource, entry] of Object.entries(storage)) {
            if (!entry || typeof entry !== 'object') {
                return {
                    key: 'config.error.buildings.storage.max',
                    args: [building, kind, resource],
                };
            }
            if (!Number.isFinite(entry.max) || entry.max < 0) {
                return {
                    key: 'config.error.buildings.storage.max',
                    args: [building, kind, resource],
                };
            }
            if (
                entry.cap !== undefined &&
                (!Number.isFinite(entry.cap) || entry.cap < 0 || entry.cap > entry.max)
            ) {
                return {
                    key: 'config.error.buildings.storage.max',
                    args: [building, kind, resource],
                };
            }
        }
        return null;
    };

    if (!config.buildings || typeof config.buildings !== 'object') {
        return { key: 'config.error.buildings.required' };
    }
    if (!config.construction || typeof config.construction !== 'object') {
        return { key: 'config.error.construction.required' };
    }
    if (!config.construction.refund || typeof config.construction.refund !== 'object') {
        return { key: 'config.error.construction.refund.required' };
    }
    if (
        !Number.isFinite(config.construction.refund.percent) ||
        config.construction.refund.percent < 0 ||
        config.construction.refund.percent > 100
    ) {
        return { key: 'config.error.construction.refund.percent' };
    }
    if (typeof config.construction.refund.allowInProgress !== 'boolean') {
        return { key: 'config.error.construction.refund.allowInProgress' };
    }

    const buildingEntries: Array<[keyof GameConfig['buildings'], BuildingConfig | undefined]> = [
        ['lumberjackCamp', config.buildings.lumberjackCamp],
        ['sawmill', config.buildings.sawmill],
        ['woodHouse', config.buildings.woodHouse],
        ['stoneHouse', config.buildings.stoneHouse],
        ['farm', config.buildings.farm],
        ['huntingHut', config.buildings.huntingHut],
        ['fishingHut', config.buildings.fishingHut],
        ['smokehouse', config.buildings.smokehouse],
        ['weaver', config.buildings.weaver],
        ['blacksmith', config.buildings.blacksmith],
        ['goldMine', config.buildings.goldMine],
        ['mint', config.buildings.mint],
        ['caravanPost', config.buildings.caravanPost],
        ['stable', config.buildings.stable],
        ['quarry', config.buildings.quarry],
        ['masonry', config.buildings.masonry],
        ['charcoalPit', config.buildings.charcoalPit],
        ['ironMine', config.buildings.ironMine],
        ['smelter', config.buildings.smelter],
        ['barracks', config.buildings.barracks],
    ];

    for (const [label, entry] of buildingEntries) {
        if (!entry || typeof entry !== 'object') {
            return { key: 'config.error.buildings.entry', args: [label] };
        }
        const costError = checkCost(entry.cost, label);
        if (costError) return costError;
        const inputError = checkStorage(entry.input, label, 'input');
        if (inputError) return inputError;
        const outputError = checkStorage(entry.output, label, 'output');
        if (outputError) return outputError;
    }

    const scalingEntries: Array<[string, { a: number; b: number } | undefined]> = [
        ['woodHouse', config.buildings.woodHouse?.costScaling],
        ['stoneHouse', config.buildings.stoneHouse?.costScaling],
    ];
    for (const [label, scaling] of scalingEntries) {
        if (!scaling || typeof scaling !== 'object') {
            return { key: 'config.error.buildings.costScaling.required', args: [label] };
        }
        if (!Number.isFinite(scaling.a) || scaling.a < 0) {
            return { key: 'config.error.buildings.costScaling.a', args: [label] };
        }
        if (!Number.isFinite(scaling.b) || scaling.b < 0) {
            return { key: 'config.error.buildings.costScaling.b', args: [label] };
        }
    }

    if (!config.initial || typeof config.initial !== 'object') {
        return { key: 'config.error.initial.required' };
    }
    if (!config.ui || typeof config.ui !== 'object') {
        return { key: 'config.error.ui.required' };
    }
    if (config.ui.language !== 'en' && config.ui.language !== 'pl') {
        return { key: 'config.error.ui.language' };
    }
    if (!Number.isFinite(config.ui.storageStatsMinutes) || config.ui.storageStatsMinutes <= 0) {
        return { key: 'config.error.ui.storageStatsMinutes' };
    }
    if (!config.audio || typeof config.audio !== 'object') {
        return { key: 'config.error.audio.required' };
    }
    const audioEntries: Array<[string, { enabled: boolean; volume: number } | undefined]> = [
        ['background', config.audio.background],
        ['bell', config.audio.bell],
        ['hit', config.audio.hit],
    ];
    for (const [label, entry] of audioEntries) {
        if (!entry || typeof entry !== 'object') {
            return { key: 'config.error.audio.entry', args: [label] };
        }
        if (typeof entry.enabled !== 'boolean') {
            return { key: 'config.error.audio.enabled', args: [label] };
        }
        if (!Number.isFinite(entry.volume) || entry.volume < 0 || entry.volume > 1) {
            return { key: 'config.error.audio.volume', args: [label] };
        }
    }
    if (!config.time || typeof config.time !== 'object') {
        return { key: 'config.error.time.required' };
    }
    if (!Number.isFinite(config.time.tickSeconds) || config.time.tickSeconds <= 0) {
        return { key: 'config.error.time.tickSeconds' };
    }
    if (!Number.isFinite(config.time.ticksPerMinute) || config.time.ticksPerMinute <= 0) {
        return { key: 'config.error.time.ticksPerMinute' };
    }
    if (!Number.isFinite(config.time.ticksPerHour) || config.time.ticksPerHour <= 0) {
        return { key: 'config.error.time.ticksPerHour' };
    }
    if (!config.map || typeof config.map !== 'object') {
        return { key: 'config.error.map.required' };
    }
    if (config.map.size !== 'small' && config.map.size !== 'medium' && config.map.size !== 'huge') {
        return { key: 'config.error.map.size' };
    }
    if (config.map.seed !== undefined && !Number.isFinite(config.map.seed)) {
        return { key: 'config.error.map.seed' };
    }
    if (!config.initial.settlement || typeof config.initial.settlement !== 'object') {
        return { key: 'config.error.initial.settlement' };
    }
    if (
        !Number.isFinite(config.initial.settlement.population) ||
        config.initial.settlement.population <= 0
    ) {
        return { key: 'config.error.initial.settlement.population' };
    }
    if (
        config.initial.settlement.tier !== 'outpost' &&
        config.initial.settlement.tier !== 'town' &&
        config.initial.settlement.tier !== 'city'
    ) {
        return { key: 'config.error.initial.settlement.tier' };
    }
    if (
        !config.initial.settlement.buildings ||
        typeof config.initial.settlement.buildings !== 'object'
    ) {
        return { key: 'config.error.initial.settlement.buildings' };
    }
    const buildings = config.initial.settlement.buildings;
    if (!Number.isFinite(buildings.houses) || buildings.houses < 0) {
        return { key: 'config.error.initial.settlement.buildings.houses' };
    }
    if (!Number.isFinite(buildings.stoneHouse) || buildings.stoneHouse < 0) {
        return { key: 'config.error.initial.settlement.buildings.stoneHouse' };
    }
    if (!Number.isFinite(buildings.lumberjack) || buildings.lumberjack < 0) {
        return { key: 'config.error.initial.settlement.buildings.lumberjack.qty' };
    }
    if (!Number.isFinite(buildings.sawmill) || buildings.sawmill < 0) {
        return { key: 'config.error.initial.settlement.buildings.sawmill.qty' };
    }
    if (!Number.isFinite(buildings.farm) || buildings.farm < 0) {
        return { key: 'config.error.initial.settlement.buildings.farm.qty' };
    }
    if (!Number.isFinite(buildings.huntingHut) || buildings.huntingHut < 0) {
        return { key: 'config.error.initial.settlement.buildings.huntingHut.qty' };
    }
    if (!Number.isFinite(buildings.fishingHut) || buildings.fishingHut < 0) {
        return { key: 'config.error.initial.settlement.buildings.fishingHut.qty' };
    }
    if (!Number.isFinite(buildings.smokehouse) || buildings.smokehouse < 0) {
        return { key: 'config.error.initial.settlement.buildings.smokehouse.qty' };
    }
    if (!Number.isFinite(buildings.weaver) || buildings.weaver < 0) {
        return { key: 'config.error.initial.settlement.buildings.weaver.qty' };
    }
    if (!Number.isFinite(buildings.blacksmith) || buildings.blacksmith < 0) {
        return { key: 'config.error.initial.settlement.buildings.blacksmith.qty' };
    }
    if (!Number.isFinite(buildings.goldMine) || buildings.goldMine < 0) {
        return { key: 'config.error.initial.settlement.buildings.goldMine.qty' };
    }
    if (!Number.isFinite(buildings.mint) || buildings.mint < 0) {
        return { key: 'config.error.initial.settlement.buildings.mint.qty' };
    }
    if (!Number.isFinite(buildings.caravanPost) || buildings.caravanPost < 0) {
        return { key: 'config.error.initial.settlement.buildings.caravanPost.qty' };
    }
    if (!Number.isFinite(buildings.stable) || buildings.stable < 0) {
        return { key: 'config.error.initial.settlement.buildings.stable.qty' };
    }
    if (!Number.isFinite(buildings.quarry) || buildings.quarry < 0) {
        return { key: 'config.error.initial.settlement.buildings.quarry.qty' };
    }
    if (!Number.isFinite(buildings.masonry) || buildings.masonry < 0) {
        return { key: 'config.error.initial.settlement.buildings.masonry.qty' };
    }
    if (!Number.isFinite(buildings.charcoalPit) || buildings.charcoalPit < 0) {
        return { key: 'config.error.initial.settlement.buildings.charcoalPit.qty' };
    }
    if (!Number.isFinite(buildings.ironMine) || buildings.ironMine < 0) {
        return { key: 'config.error.initial.settlement.buildings.ironMine.qty' };
    }
    if (!Number.isFinite(buildings.smelter) || buildings.smelter < 0) {
        return { key: 'config.error.initial.settlement.buildings.smelter.qty' };
    }
    if (!Number.isFinite(buildings.barracks) || buildings.barracks < 0) {
        return { key: 'config.error.initial.settlement.buildings.barracks.qty' };
    }
    const inv = config.initial.inventory;
    if (!inv || typeof inv !== 'object') return { key: 'config.error.initial.inventory' };
    if (!Number.isFinite(inv.food) || inv.food < 0) {
        return { key: 'config.error.initial.inventory.food' };
    }
    if (!Number.isFinite(inv.fish) || inv.fish < 0) {
        return { key: 'config.error.initial.inventory.fish' };
    }
    if (!Number.isFinite(inv.tools) || inv.tools < 0) {
        return { key: 'config.error.initial.inventory.tools' };
    }
    if (!Number.isFinite(inv.planks) || inv.planks < 0) {
        return { key: 'config.error.initial.inventory.planks' };
    }
    if (!Number.isFinite(inv.wood) || inv.wood < 0) {
        return { key: 'config.error.initial.inventory.wood' };
    }
    if (!Number.isFinite(inv.stone) || inv.stone < 0) {
        return { key: 'config.error.initial.inventory.stone' };
    }
    if (!Number.isFinite(inv.bricks) || inv.bricks < 0) {
        return { key: 'config.error.initial.inventory.bricks' };
    }
    if (!Number.isFinite(inv.charcoal) || inv.charcoal < 0) {
        return { key: 'config.error.initial.inventory.charcoal' };
    }
    if (!Number.isFinite(inv.ironOre) || inv.ironOre < 0) {
        return { key: 'config.error.initial.inventory.ironOre' };
    }
    if (!Number.isFinite(inv.ironIngots) || inv.ironIngots < 0) {
        return { key: 'config.error.initial.inventory.ironIngots' };
    }
    if (!Number.isFinite(inv.fiber) || inv.fiber < 0) {
        return { key: 'config.error.initial.inventory.fiber' };
    }
    if (!Number.isFinite(inv.clothRope) || inv.clothRope < 0) {
        return { key: 'config.error.initial.inventory.clothRope' };
    }
    if (!Number.isFinite(inv.leather) || inv.leather < 0) {
        return { key: 'config.error.initial.inventory.leather' };
    }
    if (!Number.isFinite(inv.weapons) || inv.weapons < 0) {
        return { key: 'config.error.initial.inventory.weapons' };
    }
    if (!Number.isFinite(inv.goldOre) || inv.goldOre < 0) {
        return { key: 'config.error.initial.inventory.goldOre' };
    }
    if (!Number.isFinite(inv.coins) || inv.coins < 0) {
        return { key: 'config.error.initial.inventory.coins' };
    }

    const { production, defaults } = config;
    if (!production.farm || typeof production.farm !== 'object') {
        return { key: 'config.error.production.farm' };
    }
    if (!Number.isFinite(production.farm.ticksPerFarmer) || production.farm.ticksPerFarmer <= 0) {
        return { key: 'config.error.production.farm.ticksPerFarmer' };
    }
    if (!Number.isFinite(production.farm.toolUsage) || production.farm.toolUsage <= 0) {
        return { key: 'config.error.production.farm.toolUsage' };
    }
    if (!production.hunter || typeof production.hunter !== 'object') {
        return { key: 'config.error.production.hunter' };
    }
    if (
        !production.hunter.ticksPerHuntRatio ||
        typeof production.hunter.ticksPerHuntRatio !== 'object'
    ) {
        return { key: 'config.error.production.hunter.ticksPerHuntRatio' };
    }
    if (
        !Number.isFinite(production.hunter.ticksPerHuntRatio.ticks) ||
        production.hunter.ticksPerHuntRatio.ticks <= 0
    ) {
        return { key: 'config.error.production.hunter.ticksPerHuntRatio.ticks' };
    }
    if (
        !Number.isFinite(production.hunter.ticksPerHuntRatio.wildLife) ||
        production.hunter.ticksPerHuntRatio.wildLife <= 0
    ) {
        return { key: 'config.error.production.hunter.ticksPerHuntRatio.wildLife' };
    }
    if (!Number.isFinite(production.hunter.minHuntTicks) || production.hunter.minHuntTicks <= 0) {
        return { key: 'config.error.production.hunter.minHuntTicks' };
    }
    if (
        !Number.isFinite(production.hunter.foodPerWildLife) ||
        production.hunter.foodPerWildLife <= 0
    ) {
        return { key: 'config.error.production.hunter.foodPerWildLife' };
    }
    if (
        !Number.isFinite(production.hunter.leatherPerWildLife) ||
        production.hunter.leatherPerWildLife < 0
    ) {
        return { key: 'config.error.production.hunter.leatherPerWildLife' };
    }
    if (!Number.isFinite(production.hunter.toolUsage) || production.hunter.toolUsage <= 0) {
        return { key: 'config.error.production.hunter.toolUsage' };
    }
    if (!production.fishing || typeof production.fishing !== 'object') {
        return { key: 'config.error.production.fishing' };
    }
    if (
        !Number.isFinite(production.fishing.ticksPerCatch) ||
        production.fishing.ticksPerCatch <= 0
    ) {
        return { key: 'config.error.production.fishing.ticksPerCatch' };
    }
    if (!Number.isFinite(production.fishing.fishPerCatch) || production.fishing.fishPerCatch <= 0) {
        return { key: 'config.error.production.fishing.fishPerCatch' };
    }
    if (!production.smokehouse || typeof production.smokehouse !== 'object') {
        return { key: 'config.error.production.smokehouse' };
    }
    if (
        !Number.isFinite(production.smokehouse.ticksPerBatch) ||
        production.smokehouse.ticksPerBatch <= 0
    ) {
        return { key: 'config.error.production.smokehouse.ticksPerBatch' };
    }
    if (
        !Number.isFinite(production.smokehouse.fishPerBatch) ||
        production.smokehouse.fishPerBatch <= 0
    ) {
        return { key: 'config.error.production.smokehouse.fishPerBatch' };
    }
    if (
        !Number.isFinite(production.smokehouse.foodPerBatch) ||
        production.smokehouse.foodPerBatch <= 0
    ) {
        return { key: 'config.error.production.smokehouse.foodPerBatch' };
    }
    if (!production.blacksmith || typeof production.blacksmith !== 'object') {
        return { key: 'config.error.production.blacksmith' };
    }
    if (!Number.isFinite(production.blacksmith.toolTicks) || production.blacksmith.toolTicks <= 0) {
        return { key: 'config.error.production.blacksmith.toolTicks' };
    }
    if (
        !Number.isFinite(production.blacksmith.toolIngots) ||
        production.blacksmith.toolIngots <= 0
    ) {
        return { key: 'config.error.production.blacksmith.toolIngots' };
    }
    if (!Number.isFinite(production.blacksmith.toolWood) || production.blacksmith.toolWood <= 0) {
        return { key: 'config.error.production.blacksmith.toolWood' };
    }
    if (
        !Number.isFinite(production.blacksmith.toolsPerBatch) ||
        production.blacksmith.toolsPerBatch <= 0
    ) {
        return { key: 'config.error.production.blacksmith.toolsPerBatch' };
    }
    if (
        !Number.isFinite(production.blacksmith.weaponTicks) ||
        production.blacksmith.weaponTicks <= 0
    ) {
        return { key: 'config.error.production.blacksmith.weaponTicks' };
    }
    if (
        !Number.isFinite(production.blacksmith.weaponIngots) ||
        production.blacksmith.weaponIngots <= 0
    ) {
        return { key: 'config.error.production.blacksmith.weaponIngots' };
    }
    if (
        !Number.isFinite(production.blacksmith.weaponWood) ||
        production.blacksmith.weaponWood <= 0
    ) {
        return { key: 'config.error.production.blacksmith.weaponWood' };
    }
    if (
        !Number.isFinite(production.blacksmith.weaponLeather) ||
        production.blacksmith.weaponLeather <= 0
    ) {
        return { key: 'config.error.production.blacksmith.weaponLeather' };
    }
    if (
        !Number.isFinite(production.blacksmith.weaponsPerBatch) ||
        production.blacksmith.weaponsPerBatch <= 0
    ) {
        return { key: 'config.error.production.blacksmith.weaponsPerBatch' };
    }
    if (!production.goldMine || typeof production.goldMine !== 'object') {
        return { key: 'config.error.production.goldMine' };
    }
    if (!Number.isFinite(production.goldMine.ticksPerOre) || production.goldMine.ticksPerOre <= 0) {
        return { key: 'config.error.production.goldMine.ticksPerOre' };
    }
    if (!production.mint || typeof production.mint !== 'object') {
        return { key: 'config.error.production.mint' };
    }
    if (!Number.isFinite(production.mint.ticksPerBatch) || production.mint.ticksPerBatch <= 0) {
        return { key: 'config.error.production.mint.ticksPerBatch' };
    }
    if (!production.weaver || typeof production.weaver !== 'object') {
        return { key: 'config.error.production.weaver' };
    }
    if (!Number.isFinite(production.weaver.ticksPerBatch) || production.weaver.ticksPerBatch <= 0) {
        return { key: 'config.error.production.weaver.ticksPerBatch' };
    }
    if (!production.quarry || typeof production.quarry !== 'object') {
        return { key: 'config.error.production.quarry' };
    }
    if (!Number.isFinite(production.quarry.ticksPerStone) || production.quarry.ticksPerStone <= 0) {
        return { key: 'config.error.production.quarry.ticksPerStone' };
    }
    if (!production.masonry || typeof production.masonry !== 'object') {
        return { key: 'config.error.production.masonry' };
    }
    if (
        !Number.isFinite(production.masonry.ticksPerBrick) ||
        production.masonry.ticksPerBrick <= 0
    ) {
        return { key: 'config.error.production.masonry.ticksPerBrick' };
    }
    if (!production.charcoal || typeof production.charcoal !== 'object') {
        return { key: 'config.error.production.charcoal' };
    }
    if (
        !Number.isFinite(production.charcoal.ticksPerBatch) ||
        production.charcoal.ticksPerBatch <= 0
    ) {
        return { key: 'config.error.production.charcoal.ticksPerBatch' };
    }
    if (!production.ironMine || typeof production.ironMine !== 'object') {
        return { key: 'config.error.production.ironMine' };
    }
    if (!Number.isFinite(production.ironMine.ticksPerOre) || production.ironMine.ticksPerOre <= 0) {
        return { key: 'config.error.production.ironMine.ticksPerOre' };
    }
    if (!production.smelter || typeof production.smelter !== 'object') {
        return { key: 'config.error.production.smelter' };
    }
    if (
        !Number.isFinite(production.smelter.ticksPerBatch) ||
        production.smelter.ticksPerBatch <= 0
    ) {
        return { key: 'config.error.production.smelter.ticksPerBatch' };
    }
    if (!Number.isFinite(production.smelter.orePerBatch) || production.smelter.orePerBatch <= 0) {
        return { key: 'config.error.production.smelter.orePerBatch' };
    }
    if (
        !Number.isFinite(production.smelter.charcoalPerBatch) ||
        production.smelter.charcoalPerBatch <= 0
    ) {
        return { key: 'config.error.production.smelter.charcoalPerBatch' };
    }
    if (
        !Number.isFinite(production.smelter.ingotsPerBatch) ||
        production.smelter.ingotsPerBatch <= 0
    ) {
        return { key: 'config.error.production.smelter.ingotsPerBatch' };
    }

    if (!Number.isFinite(production.lumberWorkPerWood) || production.lumberWorkPerWood <= 0) {
        return { key: 'config.error.production.lumberWorkPerWood' };
    }
    if (!Number.isFinite(production.sawmillWorkPerBatch) || production.sawmillWorkPerBatch <= 0) {
        return { key: 'config.error.production.sawmillWorkPerBatch' };
    }
    if (!Number.isFinite(production.builderWorkPerTick) || production.builderWorkPerTick <= 0) {
        return { key: 'config.error.production.builderWorkPerTick' };
    }
    if (!production.forester || typeof production.forester !== 'object') {
        return { key: 'config.error.production.forester' };
    }
    if (
        !Number.isFinite(production.forester.ticksPerTree) ||
        production.forester.ticksPerTree <= 0
    ) {
        return { key: 'config.error.production.forester.ticksPerTree' };
    }
    if (
        !Number.isFinite(production.populationGrowthPerTick) ||
        production.populationGrowthPerTick < 0
    ) {
        return { key: 'config.error.production.populationGrowthPerTick' };
    }
    if (!Number.isFinite(defaults.populationPerHouse) || defaults.populationPerHouse <= 0) {
        return { key: 'config.error.defaults.populationPerHouse' };
    }
    if (!Number.isFinite(defaults.houseFoodTargetHours) || defaults.houseFoodTargetHours <= 0) {
        return { key: 'config.error.defaults.houseFoodTargetHours' };
    }
    if (!Number.isFinite(defaults.houseHeatTargetHours) || defaults.houseHeatTargetHours <= 0) {
        return { key: 'config.error.defaults.houseHeatTargetHours' };
    }
    if (!config.population || typeof config.population !== 'object') {
        return { key: 'config.error.population.required' };
    }
    if (!config.population.hunger || typeof config.population.hunger !== 'object') {
        return { key: 'config.error.population.hunger.required' };
    }
    if (
        !Number.isFinite(config.population.hunger.hungerInterval) ||
        config.population.hunger.hungerInterval <= 0
    ) {
        return { key: 'config.error.population.hunger.hungerInterval' };
    }
    if (
        !Number.isFinite(config.population.hunger.hungerStep) ||
        config.population.hunger.hungerStep <= 0
    ) {
        return { key: 'config.error.population.hunger.hungerStep' };
    }
    if (
        !Number.isFinite(config.population.hunger.maxHunger) ||
        config.population.hunger.maxHunger <= 0
    ) {
        return { key: 'config.error.population.hunger.maxHunger' };
    }
    if (
        !Number.isFinite(config.population.hunger.eatThreshold) ||
        config.population.hunger.eatThreshold <= 0
    ) {
        return { key: 'config.error.population.hunger.eatThreshold' };
    }
    if (
        !Number.isFinite(config.population.hunger.foodStep) ||
        config.population.hunger.foodStep <= 0
    ) {
        return { key: 'config.error.population.hunger.foodStep' };
    }
    if (
        !Number.isFinite(config.population.hunger.foodQty) ||
        config.population.hunger.foodQty <= 0
    ) {
        return { key: 'config.error.population.hunger.foodQty' };
    }
    if (!config.population.warmth || typeof config.population.warmth !== 'object') {
        return { key: 'config.error.population.warmth.required' };
    }
    if (
        !Number.isFinite(config.population.warmth.maxWarmth) ||
        config.population.warmth.maxWarmth <= 0
    ) {
        return { key: 'config.error.population.warmth.maxWarmth' };
    }
    if (
        !Number.isFinite(config.population.warmth.coldInterval) ||
        config.population.warmth.coldInterval <= 0
    ) {
        return { key: 'config.error.population.warmth.coldInterval' };
    }
    if (
        !Number.isFinite(config.population.warmth.coldStep) ||
        config.population.warmth.coldStep <= 0
    ) {
        return { key: 'config.error.population.warmth.coldStep' };
    }
    if (
        !Number.isFinite(config.population.warmth.warmthStep) ||
        config.population.warmth.warmthStep <= 0
    ) {
        return { key: 'config.error.population.warmth.warmthStep' };
    }
    if (
        !Number.isFinite(config.population.warmth.coldDebtThreshold) ||
        config.population.warmth.coldDebtThreshold < 0
    ) {
        return { key: 'config.error.population.warmth.coldDebtThreshold' };
    }
    if (!config.heat || typeof config.heat !== 'object') {
        return { key: 'config.error.heat.required' };
    }
    if (!Number.isFinite(config.heat.coldInterval) || config.heat.coldInterval <= 0) {
        return { key: 'config.error.heat.coldInterval' };
    }
    if (!config.heat.coldStep || typeof config.heat.coldStep !== 'object') {
        return { key: 'config.error.heat.coldStep.required' };
    }
    if (!Number.isFinite(config.heat.coldStep.wooden) || config.heat.coldStep.wooden < 0) {
        return { key: 'config.error.heat.coldStep.wooden' };
    }
    if (!Number.isFinite(config.heat.coldStep.stone) || config.heat.coldStep.stone < 0) {
        return { key: 'config.error.heat.coldStep.stone' };
    }
    if (!Number.isFinite(config.heat.warmInterval) || config.heat.warmInterval <= 0) {
        return { key: 'config.error.heat.warmInterval' };
    }
    if (!config.heat.maxWarmth || typeof config.heat.maxWarmth !== 'object') {
        return { key: 'config.error.heat.maxWarmth.required' };
    }
    if (!Number.isFinite(config.heat.maxWarmth.wooden) || config.heat.maxWarmth.wooden <= 0) {
        return { key: 'config.error.heat.maxWarmth.wooden' };
    }
    if (!Number.isFinite(config.heat.maxWarmth.stone) || config.heat.maxWarmth.stone <= 0) {
        return { key: 'config.error.heat.maxWarmth.stone' };
    }
    if (!config.heat.warmth || typeof config.heat.warmth !== 'object') {
        return { key: 'config.error.heat.warmth.required' };
    }
    if (!Number.isFinite(config.heat.warmth.wood) || config.heat.warmth.wood <= 0) {
        return { key: 'config.error.heat.warmth.wood' };
    }
    if (!Number.isFinite(config.heat.warmth.charcoal) || config.heat.warmth.charcoal <= 0) {
        return { key: 'config.error.heat.warmth.charcoal' };
    }

    if (!config.tree || typeof config.tree !== 'object') {
        return { key: 'config.error.tree.required' };
    }
    if (!Number.isFinite(config.tree.ageInterval) || config.tree.ageInterval <= 0) {
        return { key: 'config.error.tree.ageInterval' };
    }
    if (!Number.isFinite(config.tree.maxAge) || config.tree.maxAge <= 0) {
        return { key: 'config.error.tree.maxAge' };
    }
    if (
        !Number.isFinite(config.tree.peakAge) ||
        config.tree.peakAge < 0 ||
        config.tree.peakAge > config.tree.maxAge
    ) {
        return { key: 'config.error.tree.peakAge' };
    }
    if (!Number.isFinite(config.tree.cutWorkBase) || config.tree.cutWorkBase <= 0) {
        return { key: 'config.error.tree.cutWorkBase' };
    }
    if (!Number.isFinite(config.tree.cutWorkPerAge) || config.tree.cutWorkPerAge < 0) {
        return { key: 'config.error.tree.cutWorkPerAge' };
    }
    if (!Number.isFinite(config.tree.yieldBase) || config.tree.yieldBase <= 0) {
        return { key: 'config.error.tree.yieldBase' };
    }
    if (!Number.isFinite(config.tree.yieldAgeFactor) || config.tree.yieldAgeFactor < 0) {
        return { key: 'config.error.tree.yieldAgeFactor' };
    }
    if (
        !Number.isFinite(config.tree.yieldMinMultiplier) ||
        config.tree.yieldMinMultiplier < 0 ||
        config.tree.yieldMinMultiplier > 1
    ) {
        return { key: 'config.error.tree.yieldMinMultiplier' };
    }
    if (
        !Number.isFinite(config.tree.yieldTailMultiplier) ||
        config.tree.yieldTailMultiplier < 0 ||
        config.tree.yieldTailMultiplier > 1
    ) {
        return { key: 'config.error.tree.yieldTailMultiplier' };
    }
    if (!Number.isFinite(config.tree.reseedInterval) || config.tree.reseedInterval <= 0) {
        return { key: 'config.error.tree.reseedInterval' };
    }
    if (
        !Number.isFinite(config.tree.reseedThreshold) ||
        config.tree.reseedThreshold < 0 ||
        config.tree.reseedThreshold > 1
    ) {
        return { key: 'config.error.tree.reseedThreshold' };
    }
    if (!Number.isFinite(config.tree.reseedRate) || config.tree.reseedRate < 0) {
        return { key: 'config.error.tree.reseedRate' };
    }

    if (!config.wildLife || typeof config.wildLife !== 'object') {
        return { key: 'config.error.wildLife.required' };
    }
    if (!Number.isFinite(config.wildLife.matingInterval) || config.wildLife.matingInterval <= 0) {
        return { key: 'config.error.wildLife.matingInterval' };
    }
    if (!Number.isFinite(config.wildLife.ageTicks) || config.wildLife.ageTicks <= 0) {
        return { key: 'config.error.wildLife.ageTicks' };
    }
    if (
        !Number.isFinite(config.wildLife.migrationInterval) ||
        config.wildLife.migrationInterval <= 0
    ) {
        return { key: 'config.error.wildLife.migrationInterval' };
    }
    if (!Number.isFinite(config.wildLife.migrationRate) || config.wildLife.migrationRate <= 0) {
        return { key: 'config.error.wildLife.migrationRate' };
    }
    if (!Number.isFinite(config.wildLife.migrationRadius) || config.wildLife.migrationRadius < 1) {
        return { key: 'config.error.wildLife.migrationRadius' };
    }
    if (!Number.isFinite(config.wildLife.treesPerAnimal) || config.wildLife.treesPerAnimal <= 0) {
        return { key: 'config.error.wildLife.treesPerAnimal' };
    }
    if (
        !Number.isFinite(config.wildLife.biomeCapWeight) ||
        config.wildLife.biomeCapWeight < 0 ||
        config.wildLife.biomeCapWeight > 1
    ) {
        return { key: 'config.error.wildLife.biomeCapWeight' };
    }
    if (
        !Number.isFinite(config.wildLife.targetFill) ||
        config.wildLife.targetFill <= 0 ||
        config.wildLife.targetFill > 1
    ) {
        return { key: 'config.error.wildLife.targetFill' };
    }
    if (!Number.isFinite(config.wildLife.matingAgeStart) || config.wildLife.matingAgeStart < 0) {
        return { key: 'config.error.wildLife.matingAgeStart' };
    }
    if (
        !Number.isFinite(config.wildLife.fecundityPerAdult) ||
        config.wildLife.fecundityPerAdult < 0
    ) {
        return { key: 'config.error.wildLife.fecundityPerAdult' };
    }
    if (
        !Number.isFinite(config.wildLife.reproductionDensityPower) ||
        config.wildLife.reproductionDensityPower <= 0
    ) {
        return { key: 'config.error.wildLife.reproductionDensityPower' };
    }
    if (!Number.isFinite(config.wildLife.newbornAge) || config.wildLife.newbornAge < 0) {
        return { key: 'config.error.wildLife.newbornAge' };
    }
    if (
        !Number.isFinite(config.wildLife.baseMortalityPerAgeTick) ||
        config.wildLife.baseMortalityPerAgeTick < 0 ||
        config.wildLife.baseMortalityPerAgeTick > 1
    ) {
        return { key: 'config.error.wildLife.baseMortalityPerAgeTick' };
    }
    if (!Number.isFinite(config.wildLife.juvenileAgeEnd) || config.wildLife.juvenileAgeEnd < 0) {
        return { key: 'config.error.wildLife.juvenileAgeEnd' };
    }
    if (
        !Number.isFinite(config.wildLife.juvenileExtraMortality) ||
        config.wildLife.juvenileExtraMortality < 0 ||
        config.wildLife.juvenileExtraMortality > 1
    ) {
        return { key: 'config.error.wildLife.juvenileExtraMortality' };
    }
    if (
        !Number.isFinite(config.wildLife.senescentAgeStart) ||
        config.wildLife.senescentAgeStart < 0
    ) {
        return { key: 'config.error.wildLife.senescentAgeStart' };
    }
    if (
        !Number.isFinite(config.wildLife.senescentMortalitySlope) ||
        config.wildLife.senescentMortalitySlope < 0
    ) {
        return { key: 'config.error.wildLife.senescentMortalitySlope' };
    }
    if (!Number.isFinite(config.wildLife.maxAge) || config.wildLife.maxAge <= 0) {
        return { key: 'config.error.wildLife.maxAge' };
    }
    if (config.wildLife.maxAge <= config.wildLife.matingAgeStart) {
        return { key: 'config.error.wildLife.maxAge' };
    }
    if (
        !Number.isFinite(config.wildLife.overCapacityCullRate) ||
        config.wildLife.overCapacityCullRate <= 0 ||
        config.wildLife.overCapacityCullRate > 1
    ) {
        return { key: 'config.error.wildLife.overCapacityCullRate' };
    }
    if (
        !Number.isFinite(config.wildLife.colonizeEmptyChance) ||
        config.wildLife.colonizeEmptyChance < 0 ||
        config.wildLife.colonizeEmptyChance > 1
    ) {
        return { key: 'config.error.wildLife.colonizeEmptyChance' };
    }
    if (
        !Number.isFinite(config.wildLife.colonizeEmptyMin) ||
        config.wildLife.colonizeEmptyMin < 0
    ) {
        return { key: 'config.error.wildLife.colonizeEmptyMin' };
    }
    if (
        !Number.isFinite(config.wildLife.colonizeEmptyMax) ||
        config.wildLife.colonizeEmptyMax < config.wildLife.colonizeEmptyMin
    ) {
        return { key: 'config.error.wildLife.colonizeEmptyMax' };
    }
    if (!Number.isFinite(config.wildLife.huntableAgeMin) || config.wildLife.huntableAgeMin < 0) {
        return { key: 'config.error.wildLife.huntableAgeMin' };
    }
    if (!config.fish || typeof config.fish !== 'object') {
        return { key: 'config.error.fish.required' };
    }
    if (!Number.isFinite(config.fish.regenRate) || config.fish.regenRate < 0) {
        return { key: 'config.error.fish.regenRate' };
    }

    if (!config.tool || typeof config.tool !== 'object') {
        return { key: 'config.error.tool.required' };
    }
    if (!Number.isFinite(config.tool.initialDurability) || config.tool.initialDurability <= 0) {
        return { key: 'config.error.tool.initialDurability' };
    }
    if (!config.expedition || typeof config.expedition !== 'object') {
        return { key: 'config.error.expedition.required' };
    }
    if (!Number.isFinite(config.expedition.baseTicks) || config.expedition.baseTicks <= 0) {
        return { key: 'config.error.expedition.baseTicks' };
    }
    if (!Number.isFinite(config.expedition.ticksPerHex) || config.expedition.ticksPerHex <= 0) {
        return { key: 'config.error.expedition.ticksPerHex' };
    }
    if (
        !Number.isFinite(config.expedition.foodPerPopPerHex) ||
        config.expedition.foodPerPopPerHex < 0
    ) {
        return { key: 'config.error.expedition.foodPerPopPerHex' };
    }
    if (!Number.isFinite(config.expedition.toolsRequired) || config.expedition.toolsRequired < 0) {
        return { key: 'config.error.expedition.toolsRequired' };
    }
    if (!config.expedition.encounters || typeof config.expedition.encounters !== 'object') {
        return { key: 'config.error.expedition.encounters' };
    }
    if (
        !Array.isArray(config.expedition.encounters.banditLossRange) ||
        config.expedition.encounters.banditLossRange.length !== 2 ||
        !Number.isFinite(config.expedition.encounters.banditLossRange[0]) ||
        !Number.isFinite(config.expedition.encounters.banditLossRange[1])
    ) {
        return { key: 'config.error.expedition.banditLossRange' };
    }
    if (!Array.isArray(config.expedition.encounters.fatalFactions)) {
        return { key: 'config.error.expedition.fatalFactions' };
    }
    if (typeof config.expedition.encounters.stealAll !== 'boolean') {
        return { key: 'config.error.expedition.stealAll' };
    }
    if (!config.scouting || typeof config.scouting !== 'object') {
        return { key: 'config.error.scouting.required' };
    }
    if (!Number.isFinite(config.scouting.baseTicks) || config.scouting.baseTicks < 0) {
        return { key: 'config.error.scouting.baseTicks' };
    }
    if (!Number.isFinite(config.scouting.ticksPerHex) || config.scouting.ticksPerHex < 0) {
        return { key: 'config.error.scouting.ticksPerHex' };
    }
    if (!Number.isFinite(config.scouting.foodMultiplier) || config.scouting.foodMultiplier <= 0) {
        return { key: 'config.error.scouting.foodMultiplier' };
    }
    if (!Number.isFinite(config.scouting.foodStep) || config.scouting.foodStep <= 0) {
        return { key: 'config.error.scouting.foodStep' };
    }
    if (!config.battle || typeof config.battle !== 'object') {
        return { key: 'config.error.battle.required' };
    }
    if (!Number.isFinite(config.battle.roundTicks) || config.battle.roundTicks <= 0) {
        return { key: 'config.error.battle.roundTicks' };
    }
    if (!Number.isFinite(config.battle.unitsPerRound) || config.battle.unitsPerRound <= 0) {
        return { key: 'config.error.battle.unitsPerRound' };
    }
    if (!Number.isFinite(config.battle.minRounds) || config.battle.minRounds <= 0) {
        return { key: 'config.error.battle.minRounds' };
    }
    if (
        !Number.isFinite(config.battle.maxRounds) ||
        config.battle.maxRounds < config.battle.minRounds
    ) {
        return { key: 'config.error.battle.maxRounds' };
    }
    if (!Number.isFinite(config.battle.rollMin) || !Number.isFinite(config.battle.rollMax)) {
        return { key: 'config.error.battle.rollRange' };
    }
    if (config.battle.rollMin < 0 || config.battle.rollMax <= config.battle.rollMin) {
        return { key: 'config.error.battle.rollRange' };
    }
    if (!Number.isFinite(config.battle.encounterChance) || config.battle.encounterChance < 0) {
        return { key: 'config.error.battle.encounterChance' };
    }
    if (!config.battle.encounterScaling || typeof config.battle.encounterScaling !== 'object') {
        return { key: 'config.error.battle.encounterScaling' };
    }
    if (
        !Number.isFinite(config.battle.encounterScaling.maxDistance) ||
        config.battle.encounterScaling.maxDistance <= 0
    ) {
        return { key: 'config.error.battle.encounterScaling.maxDistance' };
    }
    if (
        !Number.isFinite(config.battle.encounterScaling.minChance) ||
        config.battle.encounterScaling.minChance < 0
    ) {
        return { key: 'config.error.battle.encounterScaling.minChance' };
    }
    if (
        !Number.isFinite(config.battle.encounterScaling.maxChance) ||
        config.battle.encounterScaling.maxChance < config.battle.encounterScaling.minChance
    ) {
        return { key: 'config.error.battle.encounterScaling.maxChance' };
    }
    if (
        !Number.isFinite(config.battle.encounterScaling.minCountMultiplier) ||
        config.battle.encounterScaling.minCountMultiplier <= 0
    ) {
        return { key: 'config.error.battle.encounterScaling.minCountMultiplier' };
    }
    if (
        !Number.isFinite(config.battle.encounterScaling.maxCountMultiplier) ||
        config.battle.encounterScaling.maxCountMultiplier <
            config.battle.encounterScaling.minCountMultiplier
    ) {
        return { key: 'config.error.battle.encounterScaling.maxCountMultiplier' };
    }
    if (
        !Number.isFinite(config.battle.encounterScaling.countJitter) ||
        config.battle.encounterScaling.countJitter < 0
    ) {
        return { key: 'config.error.battle.encounterScaling.countJitter' };
    }
    if (!config.battle.units || typeof config.battle.units !== 'object') {
        return { key: 'config.error.battle.units' };
    }
    if (!config.battle.enemies || typeof config.battle.enemies !== 'object') {
        return { key: 'config.error.battle.enemies' };
    }
    const validateUnit = (unit: BattleUnitTypeConfig, label: string): ConfigError | null => {
        if (!unit.id || typeof unit.id !== 'string') {
            return { key: 'config.error.battle.unit.id', args: [label] };
        }
        if (!unit.labelKey || typeof unit.labelKey !== 'string') {
            return { key: 'config.error.battle.unit.labelKey', args: [label] };
        }
        if (!Number.isFinite(unit.power) || unit.power < 0) {
            return { key: 'config.error.battle.unit.power', args: [label] };
        }
        if (!Number.isFinite(unit.foodPerHex) || unit.foodPerHex < 0) {
            return { key: 'config.error.battle.unit.foodPerHex', args: [label] };
        }
        if (!Number.isFinite(unit.weaponCost) || unit.weaponCost < 0) {
            return { key: 'config.error.battle.unit.weaponCost', args: [label] };
        }
        return null;
    };
    for (const [key, unit] of Object.entries(config.battle.units)) {
        const error = validateUnit(unit, key);
        if (error) return error;
    }
    for (const [key, enemy] of Object.entries(config.battle.enemies)) {
        const error = validateUnit(enemy, key);
        if (error) return error;
        if (!Number.isFinite(enemy.weight) || enemy.weight < 0) {
            return { key: 'config.error.battle.enemy.weight', args: [key] };
        }
        if (
            !Array.isArray(enemy.countRange) ||
            enemy.countRange.length !== 2 ||
            !Number.isFinite(enemy.countRange[0]) ||
            !Number.isFinite(enemy.countRange[1])
        ) {
            return { key: 'config.error.battle.enemy.countRange', args: [key] };
        }
    }
    if (!config.alerts || typeof config.alerts !== 'object') {
        return { key: 'config.error.alerts.required' };
    }
    if (
        !Number.isFinite(config.alerts.warningCooldownTicks) ||
        config.alerts.warningCooldownTicks < 0
    ) {
        return { key: 'config.error.alerts.warningCooldownTicks' };
    }
    if (!Number.isFinite(config.alerts.starvationHours) || config.alerts.starvationHours < 0) {
        return { key: 'config.error.alerts.starvationHours' };
    }
    if (!Number.isFinite(config.alerts.coldDebtRatio) || config.alerts.coldDebtRatio < 0) {
        return { key: 'config.error.alerts.coldDebtRatio' };
    }
    if (!config.transport || typeof config.transport !== 'object') {
        return { key: 'config.error.transport.required' };
    }
    if (!Number.isFinite(config.transport.baseTicks) || config.transport.baseTicks <= 0) {
        return { key: 'config.error.transport.baseTicks' };
    }
    if (!Number.isFinite(config.transport.ticksPerHex) || config.transport.ticksPerHex <= 0) {
        return { key: 'config.error.transport.ticksPerHex' };
    }
    if (
        !Number.isFinite(config.transport.departureInterval) ||
        config.transport.departureInterval <= 0
    ) {
        return { key: 'config.error.transport.departureInterval' };
    }

    // assistants (local logistics)
    if (!config.transport.assistants || typeof config.transport.assistants !== 'object') {
        return { key: 'config.error.transport.assistants.required' };
    }
    const dt = config.transport.assistants.distanceTicks;
    if (!dt || typeof dt !== 'object') {
        return { key: 'config.error.transport.assistants.distanceTicks' };
    }
    if (!Number.isFinite(dt.near) || dt.near <= 0) {
        return { key: 'config.error.transport.assistants.distanceTicks.near' };
    }
    if (!Number.isFinite(dt.city) || dt.city <= 0) {
        return { key: 'config.error.transport.assistants.distanceTicks.city' };
    }
    if (!Number.isFinite(dt.remote) || dt.remote <= 0) {
        return { key: 'config.error.transport.assistants.distanceTicks.remote' };
    }
    if (
        !Number.isFinite(config.transport.assistants.payloadPenaltyPercent) ||
        config.transport.assistants.payloadPenaltyPercent < 0
    ) {
        return { key: 'config.error.transport.assistants.payloadPenaltyPercent' };
    }
    const byBuilding = config.transport.assistants.distanceByBuilding;
    if (!byBuilding || typeof byBuilding !== 'object') {
        return { key: 'config.error.transport.assistants.distanceByBuilding' };
    }
    for (const [k, v] of Object.entries(byBuilding)) {
        if (v !== 'near' && v !== 'city' && v !== 'remote') {
            return { key: 'config.error.transport.assistants.distanceByBuilding.value', args: [k] };
        }
    }

    return null;
}
